import * as React from 'react';

import { deepClone } from '../../utils/object';
import {
	ValidationRule,
	FormContext,
} from './shared';


export type FormRef = {
	submitForm: (...args) => void;
	resetForm: () => void;
};

type FormProps<S> = {
	formObject: S;
	isFetching?: boolean;
	resetNoValidFormObject?: boolean;
	enableLogs?: boolean;
	component?: React.ElementType;
	data?: any;
	onSubmit?: (formObject: S, creatingObject: Partial<S>, ...args) => void;
	onChange?: (formObject: S) => void;
}

type FormState<T> = {
	formObject: T;
	referenceFormObject: T;
	isValid: boolean;
	errorValidationRule: ValidationRule | null;
}

function createForm<S>(WrappedComponent: React.ComponentType<FormProps<S>>): React.ComponentClass<FormProps<S>> {
	return class extends React.PureComponent<FormProps<S>, FormState<S>> {
		static displayName = `createForm[HOC](${WrappedComponent.displayName || WrappedComponent.name})`;
		static defaultProps = {
			onChange: () => {},
		};
		state = {
			formObject: deepClone(this.props.formObject),
			referenceFormObject: this.props.formObject,
			isValid: false,
			errorValidationRule: null
		};
		validationRules: Array<ValidationRule<any>> = [];

		static getDerivedStateFromProps(props: FormProps<S>, state: FormState<S>) {
			if (props.formObject !== state.referenceFormObject) {
				return {
					formObject: props.formObject,
					referenceFormObject: props.formObject,
				};
			}
	
			return null;
		}

		getFormObject = () => ({ ...this.state.formObject });

		setFormObject = (formObject) => {
			const { enableLogs, onChange } = this.props;

			this.setState({
				formObject: { ...formObject },
			});
			onChange({ ...formObject });
			enableLogs && console.log('formObject:', { ...formObject });
		};

		addValidationRule = (validationRule: ValidationRule<any>) => {
			this.validationRules.push(validationRule);
		};

		removeValidationRule = (validationRule: ValidationRule<any>) => {
			this.validationRules = this.validationRules.filter(vr => vr !== validationRule);
			validationRule === this.state.errorValidationRule && this.setState({
				errorValidationRule: null
			});
		};

		getErrorValidationRule = () => this.state.errorValidationRule;

		sortValidationRulesByPriority = (validationRule: ValidationRule<any>) => {
			const idx = this.validationRules.findIndex(vr => vr === validationRule);

			if (idx !== -1) {
				this.validationRules.splice(idx, 1);
				this.validationRules.unshift(validationRule);
			}
		};

		submitForm = (...args) => {
			const { resetNoValidFormObject } = this.props;
			const formObject = this.getFormObject();

			this.validateForm(formObject, (isValid: boolean) => {
				isValid
					? this.props.onSubmit(formObject, {}, ...args)
					: resetNoValidFormObject && this.setFormObject(this.props.formObject);
			});
		};

		resetForm = () => this.setFormObject(deepClone(this.props.formObject));

		validateForm = (formObject, cb?) => {
			const asyncRules = this.validationRules.filter(rule => rule.isAsync);
			const syncRules = this.validationRules.filter(rule => !rule.isAsync);
			const noValidSyncRule = syncRules.find(rule => !rule.fn(formObject)) || null;
			const updateStateAfterValidation = (rule: ValidationRule) => {
				this.setState({
					isValid: !rule,
					errorValidationRule: rule
				}, () => {
					const { isValid, errorValidationRule } = this.state;
	
					cb && cb(isValid, errorValidationRule);
				});
			};

			if (noValidSyncRule) {
				updateStateAfterValidation(noValidSyncRule);
			} else if (asyncRules.length > 0) {
				const asyncQueue = asyncRules.map(rule => {
					return new Promise<{isValid: boolean; rule: ValidationRule}>(resolve => {
						(rule.fn(formObject) as Promise<boolean>).then(isValid => {
							resolve({ isValid, rule });
						});
					});
				});
				Promise.all(asyncQueue).then(result => {
					const noValidAsyncResponse = result.find(response => !response.isValid) || null;
					const noValidAsyncRule = noValidAsyncResponse && noValidAsyncResponse.rule;

					updateStateAfterValidation(noValidAsyncRule);
				});
			} else {
				updateStateAfterValidation(null);
			}
		};

		render() {
			const { isFetching, data } = this.props;

			if (isFetching) return null;

			const formContextValue = {
				isFetching: isFetching,
				getFormObject: this.getFormObject,
				setFormObject: this.setFormObject,
				addValidationRule: this.addValidationRule,
				removeValidationRule: this.removeValidationRule,
				getErrorValidationRule: this.getErrorValidationRule,
				sortValidationRulesByPriority: this.sortValidationRulesByPriority,
				submitForm: this.submitForm,
				validateForm: this.validateForm,
				data,
			};

			return (
				<FormContext.Provider value={formContextValue}>
					<WrappedComponent {...this.props} />
				</FormContext.Provider>
			);
		}
	}
}

const Form = createForm(
	({ formObject, isFetching, resetNoValidFormObject, enableLogs, onSubmit, component, ...rest }) => {
		const Tag = component || 'div';

		return (
			<Tag {...rest} />
		);
});

export {
	createForm,
	Form,
};
